import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch, withRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import App from './App';
import AutoLogin from './base/autologin/AutoLogin';
import CheckoutPageContainer from './checkout/CheckoutPageContainer';
import ClientLanding from './client-landing';
import Operations from './operations';
import TimesheetApprovalPage from './timesheets/approval/TimesheetApprovalPage';
import { Version } from './Version';
import { SERVER_URL } from '../config';
import { modalTypes } from '../consts';
import history from '../history';
import { TRootState } from '../reducers';
import HubSpotScript from '../scripts/HubSpotScript';

const Root = (props: RouteComponentProps) => {
    const { loggedIn, clientData, user } = useSelector((state: TRootState) => ({
        loggedIn: state.clientData.loggedIn,
        clientData: state.clientData,
        user: state.user,
    }));
    const [shouldLoadHubSpotScript, setShouldLoadHubSpotScript] = useState(false);

    useEffect(() => {
        if (loggedIn) {
            const userName = clientData.userData.content.full_name;
            const userEmail = clientData.userData.content.email;
            const userType = clientData.userData.content.user_type;

            if (process.env.SENTRY_DSN) {
                Sentry.setUser({
                    email: userEmail,
                    username: userName,
                    id: userType,
                });
            }
        }
    }, []);

    // HubSpot script
    useEffect(() => {
        const shouldLoad =
            (!loggedIn && history.location.pathname === '/client') ||
            (loggedIn && user?.isClient);

        setShouldLoadHubSpotScript(shouldLoad);
    }, [loggedIn, user?.isClient]);

    const userIsClientOrAgent = user?.isAgent || user?.isClient;

    if (loggedIn && !userIsClientOrAgent) {
        window.location.href = `${SERVER_URL}`;
        return null;
    }

    return (
        <div style={{ width: '100%', height: '100%' }}>
            <Switch>
                <Route exact={true} path="/version" component={Version} />
                <Route exact={true} path="/login" component={AutoLogin} />
                <Route exact={true} path="/timesheets/approve" component={TimesheetApprovalPage} />
                <Redirect exact={true} from="/" to="/client" />
                <Route exact={true} path="/client" component={ClientLanding} />
                <Route
                    exact={true}
                    path="/client/register"
                    render={() => (
                        <ClientLanding
                            // @ts-ignore
                            showModal={modalTypes.SIGNUP}
                            {...props}
                        />
                    )}
                />
                <Route
                    exact={true}
                    path="/client/login"
                    render={() => (
                        <ClientLanding
                            // @ts-ignore
                            showModal={modalTypes.LOGIN}
                            {...props}
                        />
                    )}
                />
                <Route path="/job/create" component={App} />
                <Route path="/job/edit/:jobId" component={App} />
                <Route
                    path="/job/checkout/:jobId"
                    render={() =>
                        loggedIn ? (
                            <CheckoutPageContainer />
                        ) : (
                            <Redirect to={`/client/login/?redirect=${history.location.pathname}`} />
                        )
                    }
                />
                <Route
                    path="/account"
                    render={() =>
                        loggedIn ? <Operations /> : <Redirect to="/" />
                    }
                />
                <Route
                    path="/freelancers"
                    render={() =>
                        loggedIn ? <Operations /> : <Redirect to="/" />
                    }
                />
                <Route
                    path="/induction-session-calendar"
                    render={() =>
                        loggedIn ? <Operations /> : <Redirect to="/" />
                    }
                />
                <Route
                    path="/availability-lists"
                    render={() =>
                        loggedIn ? <Operations /> : <Redirect to="/" />
                    }
                />
                <Route
                    path="/waiting-room"
                    render={() =>
                        loggedIn ? <Operations /> : <Redirect to="/" />
                    }
                />
                <Route
                    path="/clients"
                    render={() =>
                        loggedIn ? <Operations /> : <Redirect to="/" />
                    }
                />
                <Route
                    path="/shiftmanagement/:route?"
                    render={() =>
                        loggedIn ? <Operations /> : <Redirect to="/" />
                    }
                />
                <Route
                    path="/jobs/:route?"
                    render={() =>
                        loggedIn ? <Operations /> : <Redirect to="/" />
                    }
                />
                <Route
                    path="/series/:route?"
                    render={() =>
                        loggedIn ? <Operations /> : <Redirect to="/" />
                    }
                />
                <Route
                    path="/payments/:route?/:method?/:id?"
                    render={() =>
                        loggedIn ? <Operations /> : <Redirect to="/" />
                    }
                />
                <Route
                    path="/admin/:route?/:id?"
                    render={() =>
                        loggedIn ? <Operations /> : <Redirect to="/" />
                    }
                />
                <Route
                    path="/operations/:route?/:id?"
                    render={() =>
                        loggedIn ? <Operations /> : <Redirect to="/" />
                    }
                />
                <Route
                    path="/static/:route?"
                    render={() =>
                        loggedIn ? <Operations /> : <Redirect to="/" />
                    }
                />
                <Route
                    path="/contact-us"
                    render={() =>
                        loggedIn ? <Operations /> : <Redirect to="/" />
                    }
                />
            </Switch>
            <HubSpotScript shouldLoadScript={shouldLoadHubSpotScript} />
        </div>
    );
};

export default withRouter(Root);
